/**
 * Pure movement math for the character controller. No Babylon imports so it
 * unit-tests headlessly. All vectors are {x, z} ground-plane pairs; vertical
 * motion is a scalar handled separately.
 */

export interface Vec2 {
  x: number;
  z: number;
}

/** Rotate a local input axis pair (strafe, forward) into world space by yaw. */
export function inputToWorldWish(axisX: number, axisY: number, yaw: number): Vec2 {
  const sin: number = Math.sin(yaw);
  const cos: number = Math.cos(yaw);
  // forward = (sin, cos), right = (cos, -sin)
  const x: number = axisY * sin + axisX * cos;
  const z: number = axisY * cos - axisX * sin;
  const len: number = Math.hypot(x, z);
  if (len <= 1) {
    return { x, z };
  }
  return { x: x / len, z: z / len };
}

/** Accelerate velocity toward wish * maxSpeed, clamped. */
export function accelerate(
  vel: Vec2,
  wish: Vec2,
  accel: number,
  maxSpeed: number,
  dt: number
): Vec2 {
  const targetX: number = wish.x * maxSpeed;
  const targetZ: number = wish.z * maxSpeed;
  const step: number = accel * dt;
  return {
    x: moveToward(vel.x, targetX, step),
    z: moveToward(vel.z, targetZ, step),
  };
}

/** Exponential-ish friction when there is no input. */
export function applyFriction(vel: Vec2, friction: number, dt: number): Vec2 {
  const speed: number = Math.hypot(vel.x, vel.z);
  if (speed === 0) {
    return { x: 0, z: 0 };
  }
  const drop: number = friction * dt;
  const newSpeed: number = Math.max(0, speed - drop);
  const scale: number = newSpeed / speed;
  return { x: vel.x * scale, z: vel.z * scale };
}

function moveToward(current: number, target: number, maxDelta: number): number {
  const delta: number = target - current;
  if (Math.abs(delta) <= maxDelta) {
    return target;
  }
  return current + Math.sign(delta) * maxDelta;
}

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/** Sloped conveyor walk surface along z: sits at y0 for z <= z0, rises
 *  linearly to y1 at z >= z1. */
export interface ConveyorSurface {
  z0: number;
  y0: number;
  z1: number;
  y1: number;
}

/** How far below/above the sloped surface the conveyor still grabs feet.
 *  Below covers float error; above covers a jump mid-ride. */
export const CONVEYOR_SURFACE_BAND_BELOW = 0.5;
export const CONVEYOR_SURFACE_BAND_ABOVE = 1.4;

/** Whether a conveyor volume grabs a player at (px, pz) with feet at feetY.
 *  Without a surface it is a plain AABB test. With one, the feet must also
 *  sit within a narrow band of the sloped surface, so players standing under
 *  (or jumping far above) a raised ramp section are unaffected. */
export function conveyorGrabsFeet(
  px: number,
  feetY: number,
  pz: number,
  min: Vec3Like,
  max: Vec3Like,
  surface?: ConveyorSurface
): boolean {
  if (
    px < min.x || px > max.x ||
    feetY < min.y || feetY > max.y ||
    pz < min.z || pz > max.z
  ) {
    return false;
  }
  if (surface !== undefined) {
    const t: number = Math.min(1, Math.max(0, (pz - surface.z0) / (surface.z1 - surface.z0)));
    const surfaceY: number = surface.y0 + (surface.y1 - surface.y0) * t;
    if (
      feetY < surfaceY - CONVEYOR_SURFACE_BAND_BELOW ||
      feetY > surfaceY + CONVEYOR_SURFACE_BAND_ABOVE
    ) {
      return false;
    }
  }
  return true;
}

/** Integrate vertical velocity under gravity; returns [newVy, dy]. */
export function integrateVertical(
  vy: number,
  gravity: number,
  dt: number
): { vy: number; dy: number } {
  const newVy: number = vy - gravity * dt;
  const dy: number = ((vy + newVy) / 2) * dt;
  return { vy: newVy, dy };
}
